import React, { useCallback, useEffect, useState } from 'react';
import { getAuthors, createAuthor, updateAuthor, setAuthorActive } from '../../api/cmsContents';

/**
 * 저자 관리.
 *
 * 기술노트는 저자가 **발행 조건**이다(06 N1). 검색과 AI 검색이 "누가 쓴 글인가"를
 * 신뢰 신호로 쓰고, `Article` JSON-LD 의 `author` 가 비면 리치 결과에서 빠진다.
 *
 * 관리자 계정과 분리돼 있다 — 글쓴이는 로그인 계정과 1:1 이 아니다. 계정이 중지돼도
 * 글쓴이 표기는 남아야 하고, 외부 기고자도 있을 수 있다. 그래서 **지우지 않고
 * 비활성만** 한다.
 */
const EMPTY = { slug: '', displayName: '', roleTitle: '', bio: '' };

const AuthorManager = ({ isOpen, onClose, onChanged }) => {
    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editId, setEditId] = useState(null); // number | 'new' | null
    const [draft, setDraft] = useState(EMPTY);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            setAuthors(await getAuthors());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

    if (!isOpen) return null;

    const run = async (fn) => {
        setError('');
        try {
            await fn();
            setDirty(true);
            await load();
            setEditId(null);
            setDraft(EMPTY);
            return true;
        } catch (e) {
            setError(e.message);
            return false;
        }
    };

    const save = () => run(async () => {
        if (editId === 'new') await createAuthor(draft);
        else await updateAuthor(editId, draft);
    });

    const close = () => {
        if (dirty) onChanged?.();
        setDirty(false);
        setEditId(null);
        setDraft(EMPTY);
        onClose();
    };

    const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={close}>
            <div
                className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                    <div>
                        <h3 className="text-lg font-extrabold text-gray-800 m-0">저자 관리</h3>
                        <p className="text-xs text-gray-500 mt-1">
                            기술노트는 저자가 발행 조건입니다. 검색이 &lsquo;누가 쓴 글인가&rsquo;를 신뢰 신호로 씁니다.
                        </p>
                    </div>
                    <button type="button" onClick={close} aria-label="닫기" className="w-10 h-10 rounded-lg text-gray-500 hover:bg-gray-100">✕</button>
                </div>

                <div className="px-6 py-4">
                    {error && (
                        <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 whitespace-pre-line">{error}</div>
                    )}

                    {loading ? (
                        <p className="py-8 text-center text-sm text-gray-400 animate-pulse">불러오는 중…</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-bold text-gray-500 border-b border-gray-200">
                                    <th className="py-2">이름</th>
                                    <th className="py-2 w-36">주소</th>
                                    <th className="py-2 w-40">직함</th>
                                    <th className="py-2 w-16">글</th>
                                    <th className="py-2 w-32 text-right">관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {authors.length === 0 && editId !== 'new' && (
                                    <tr><td colSpan={5} className="py-8 text-center text-gray-500">
                                        아직 저자가 없습니다. 기술노트를 발행하려면 먼저 저자를 등록하세요.
                                    </td></tr>
                                )}

                                {authors.map((a) => (
                                    <tr key={a.id} className={`border-b border-gray-100 ${a.isActive ? '' : 'text-gray-400'}`}>
                                        {editId === a.id ? (
                                            <>
                                                <td className="py-2 pr-2"><input className={input} value={draft.displayName} onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} /></td>
                                                <td className="py-2 pr-2"><input className={`${input} font-mono text-xs`} value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} /></td>
                                                <td className="py-2 pr-2" colSpan={2}><input className={input} value={draft.roleTitle} onChange={(e) => setDraft({ ...draft, roleTitle: e.target.value })} /></td>
                                                <td className="py-2 text-right whitespace-nowrap">
                                                    <button type="button" onClick={save} className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-bold mr-1">저장</button>
                                                    <button type="button" onClick={() => { setEditId(null); setDraft(EMPTY); }} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-bold">취소</button>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="py-2 font-semibold text-gray-900">
                                                    {a.displayName}
                                                    {!a.isActive && <span className="ml-2 text-[11px] font-normal">(중지)</span>}
                                                </td>
                                                <td className="py-2 font-mono text-xs text-gray-500">{a.slug}</td>
                                                <td className="py-2 text-gray-600">{a.roleTitle || '—'}</td>
                                                <td className="py-2 text-gray-500">{a.contentCount}</td>
                                                <td className="py-2 text-right whitespace-nowrap">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setEditId(a.id); setDraft({ slug: a.slug, displayName: a.displayName, roleTitle: a.roleTitle || '', bio: a.bio || '' }); }}
                                                        className="px-2 py-1 text-xs font-bold text-gray-700 hover:text-primary-500"
                                                    >수정</button>
                                                    <button
                                                        type="button"
                                                        onClick={() => run(() => setAuthorActive(a.id, !a.isActive))}
                                                        className={`px-2 py-1 text-xs font-bold ${a.isActive ? 'text-red-600' : 'text-emerald-600'}`}
                                                    >{a.isActive ? '중지' : '사용'}</button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}

                                {editId === 'new' && (
                                    <tr className="border-b border-gray-100 bg-primary-50/40">
                                        <td className="py-2 pr-2"><input className={input} placeholder="이름 (예: 김지웅)" value={draft.displayName} onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} /></td>
                                        <td className="py-2 pr-2"><input className={`${input} font-mono text-xs`} placeholder="jiwoong" value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} /></td>
                                        <td className="py-2 pr-2" colSpan={2}><input className={input} placeholder="직함 (예: 대표 / ESL 컨설턴트)" value={draft.roleTitle} onChange={(e) => setDraft({ ...draft, roleTitle: e.target.value })} /></td>
                                        <td className="py-2 text-right whitespace-nowrap">
                                            <button type="button" onClick={save} className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-bold mr-1">추가</button>
                                            <button type="button" onClick={() => { setEditId(null); setDraft(EMPTY); }} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-bold">취소</button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    <p className="mt-3 text-xs text-gray-500 leading-relaxed">
                        저자는 <b className="text-gray-700">지우지 않고 중지</b>합니다. 발행된 글의 글쓴이 표기가 사라지면
                        그 글의 신뢰 신호도 같이 사라집니다.
                    </p>
                </div>

                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/60">
                    <button
                        type="button"
                        onClick={() => { setEditId('new'); setDraft(EMPTY); }}
                        disabled={editId === 'new'}
                        className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-bold disabled:opacity-40"
                    >+ 저자 추가</button>
                    <button type="button" onClick={close} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-bold">닫기</button>
                </div>
            </div>
        </div>
    );
};

export default AuthorManager;
